/**
 * Chat Input Component
 * Input field for user messages
 */

import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { MESSAGE_CONFIG } from '../config';
import { sanitizeChatInput } from '../utils/sanitization';

interface ChatInputProps {
  onSend: (message: string) => Promise<boolean>;
  disabled?: boolean;
  placeholder?: string;
  sendMessageAriaLabel?: string;
  maxCharsHintTemplate?: string;
  charLimitExceededSuffix?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  sendMessageAriaLabel = 'Send message',
  maxCharsHintTemplate = 'Maximum {max} characters allowed.',
  charLimitExceededSuffix = 'Character limit exceeded',
}) => {
  const [message, setMessage] = useState('');
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const maxLength = MESSAGE_CONFIG.maxLength;
  const charCount = message.length;

  useEffect(() => {
    // Auto-focus input when component mounts; skipped on mobile so the keyboard doesn't open unprompted
    if (inputRef.current && !disabled && !isMobile) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSend = async () => {
    const sanitizedMessage = sanitizeChatInput(message).trim();

    if (sanitizedMessage && !disabled) {
      setMessage('');
      setShowLimitMessage(false);
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }

      const isSent = await onSend(sanitizedMessage);

      if (!isSent && inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const isTruncated = value.trim().length > maxLength;
    const nextValue = isTruncated ? value.trim().slice(0, maxLength) : value;

    setMessage(nextValue);
    setShowLimitMessage(isTruncated);

    // Auto-resize textarea
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  };

  return (
    <div className={`chatbot-input-container ${showLimitMessage ? 'chatbot-input-container-error' : ''}`}>
      <textarea
        ref={inputRef}
        name='chatbot-input'
        className="chatbot-input"
        value={message}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        // readOnly keeps focus while loading; disabled ejects it and the SR re-reads the dialog
        readOnly={disabled}
        rows={1}
        aria-invalid={showLimitMessage}
        aria-describedby="chatbot-char-limit-hint"
      />
      <button
        className="chatbot-send-button"
        onClick={() => {
          void handleSend();
        }}
        disabled={disabled || !message.trim()}
        aria-label={sendMessageAriaLabel}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="22" y1="2" x2="11" y2="13"></line>
          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
        </svg>
      </button>
      <span id="chatbot-char-limit-hint" className="chatbot-visually-hidden">
        {maxCharsHintTemplate.replace('{maxChar}', `${maxLength}`)}
      </span>
      {showLimitMessage && (
        <div className="chatbot-input-limit-error" role="alert">
          {`${charCount}/${maxLength} ${charLimitExceededSuffix}`}
        </div>
      )}
    </div>
  );
};
